import { CommunicationEventPurposePropertyTypes, StudentCommunicationEventProperties } from './../../model/student-communication-events';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Student } from 'src/app/model/student/student.model';
import { StudentCommunicationEvent } from '../../model/student-communication-events';
import { StudentCommunicationEventsService } from '../../service/student-communication-events.service';
import { FormBuilder, FormGroup, FormGroupDirective, Validators } from '@angular/forms';
import { GetPropertyPipe } from 'src/app/pipes/get-property.pipe';
import { MsgModel } from 'src/app/component/common-components/msg/model/msg.model';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';

@Component({
    selector: 'create-edit-communication-events',
    templateUrl: './create-edit-communication-events.component.html',
    styleUrls: ['./create-edit-communication-events.component.scss']
})
export class CreateEditCommunicationEventsComponent implements OnInit {
    @Input() students: Student[];
    @Input() studentCommunicationEvents: StudentCommunicationEvent[];
    @Output() returnOutput: EventEmitter<any> = new EventEmitter<any>();

    title: string = '';

    @ViewChild('communicationEventsForm') communicationEventsForm: FormGroupDirective;
    form: FormGroup;

    communicationEventPurposeTypes: CommunicationEventPurposePropertyTypes[];

    communicationDateOptions = {
        maxDate: new Date(),
        startAt: new Date()
    };

    isLoading: boolean = false;
    isSaving: boolean = false;
    isSubmitting: boolean = false;

    promises: Array<any> = [];

    msg: MsgModel;

    getPropertyPipe = new GetPropertyPipe();

    constructor(private dialog: MatDialog, private studentCommunicationEventsService: StudentCommunicationEventsService, private fb: FormBuilder) {
        this.form = fb.group({
            communicationEventPurposeType: ['', [Validators.required]],
            communicationDate: ['', [Validators.required]],
            comments: ['', [Validators.required]],
            familyPresent: ['']
        });
    }

    ngOnInit(): void {
        this.isLoading = true;

        // Reference API calls
        this.getCommunicationEventPurposeTypes();

        Promise.all(this.promises).then((res) => {
            if (this.studentCommunicationEvents) {
                this.initEdit();
            } else {
                this.initAdd();
            }

            this.isLoading = false;
        });
    }

    getCommunicationEventPurposeTypes() {
        let deferred = new Promise((resolve, reject) => {
            this.studentCommunicationEventsService.getCommunicationEventPurposeTypes().subscribe((res) => {
                if (res.communicationEventPurposeTypes) {
                    this.communicationEventPurposeTypes = res.communicationEventPurposeTypes;
                    resolve(res);
                } else if (res?.msg) {
                    reject(res);
                } else {
                    reject('err');
                }
            });
        });

        this.promises.push(deferred);
    }

    initAdd() {
        this.title = 'Add (' + this.students.length + ') new Communication Event' + (this.students.length > 1 ? 's' : '');
    }

    initEdit() {
        this.title =
            'Editing Communication Event for ' + this.studentCommunicationEvents.length + ' student' + (this.studentCommunicationEvents.length > 1 ? 's' : '');

        this.setForm();
    }

    setForm() {
        let studentCommunicationEvent = this.studentCommunicationEvents[0];

        this.form.get('communicationDate')?.setValue(studentCommunicationEvent.COMMUNICATION_DATE);
        if (studentCommunicationEvent.COMMUNICATION_EVENT_PURPOSE_TYPES) {
            let communicationEventPurposeType = this.communicationEventPurposeTypes.find(
                (cept) => cept.ID === studentCommunicationEvent.COMMUNICATION_EVENT_PURPOSE_TYPES.ID
            );
            this.form.get('communicationEventPurposeType')?.setValue(communicationEventPurposeType);
        }
        this.form.get('comments')?.setValue(studentCommunicationEvent.COMMENTS);
        if (this.getPropertyPipe.transform(studentCommunicationEvent, 'STUDENT_COMMUNICATION_EVENT_PROPERTIES[0]')) {
            this.form.get('familyPresent')?.setValue(true);
        }
    }

    submit() {
        if (this.form.valid) {
            this.isSubmitting = true;
            let payload = this.generatePayload();

            //console.warn(payload);

            // Create Mode
            if (!this.studentCommunicationEvents) {
                this.studentCommunicationEventsService.createStudentCommunicationEvents(payload).subscribe({
                    next: (res) => {
                        if (res.msg) {
                            if (res.msg.SUCCESS) {
                                setTimeout(() => {
                                    this.returnOutput.emit({ success: true });
                                }, 500);
                            } else {
                                this.msg = { SUCCESS: false, MSG: res.msg };
                            }
                        } else {
                            this.msg = { SUCCESS: false, MSG: 'Error creating communication events.' };
                        }
                    },
                    error: (err) => {
                        this.msg = { SUCCESS: false, MSG: 'Error creating communication events.' };
                    },
                    complete: () => {
                        this.isSubmitting = false;
                    }
                });
            } else {
                this.studentCommunicationEventsService.updateStudentCommunicationEvents(payload).subscribe({
                    next: (res) => {
                        if (res.msg) {
                            if (res.msg.SUCCESS) {
                                setTimeout(() => {
                                    this.returnOutput.emit({ success: true });
                                }, 500);
                            } else {
                                this.msg = { SUCCESS: false, MSG: res.msg };
                            }
                        } else {
                            this.msg = { SUCCESS: false, MSG: 'Error updating communication events.' };
                        }
                    },
                    error: (err) => {
                        this.msg = { SUCCESS: false, MSG: 'Error updating communication events.' };
                    },
                    complete: () => {
                        this.isSubmitting = false;
                    }
                });
            }
        }
    }

    generatePayload() {
        let payload: any = {};

        let newStudentCommunicationEvent = new StudentCommunicationEvent();
        newStudentCommunicationEvent.COMMUNICATION_DATE = this.form.get('communicationDate')?.value;
        newStudentCommunicationEvent.COMMUNICATION_EVENT_PURPOSE_TYPES = this.form.get('communicationEventPurposeType')?.value;
        newStudentCommunicationEvent.COMMENTS = this.form.get('comments')?.value;

        if (this.form.get('familyPresent')?.value) {
            newStudentCommunicationEvent.STUDENT_COMMUNICATION_EVENT_PROPERTIES = [];
            let newStudentCommunicationEventProperties = new StudentCommunicationEventProperties();

            newStudentCommunicationEventProperties = this.getPropertyPipe.transform(
                this.form.get('communicationEventPurposeType')?.value,
                'COMMUNICATION_EVENT_PURPOSE_PROPERTIES[0]'
            );

            newStudentCommunicationEvent.STUDENT_COMMUNICATION_EVENT_PROPERTIES.push(newStudentCommunicationEventProperties);
        }

        payload.studentCommunicationEvent = newStudentCommunicationEvent;

        // Create Mode
        if (!this.studentCommunicationEvents) {
            payload.students = this.students;
        }
        // Edit Mode
        else {
            payload.studentCommunicationEvents = this.studentCommunicationEvents;
        }

        return payload;
    }

    communicationEventPurposeTypeChange(event: any) {}

    cancel() {
        this.returnOutput.emit({ canceled: true });
    }
}
